import { Fragment, useEffect, useRef, useState } from "react";

import Div from "theme/layouts/Div";
import Events from "core-web/libs/Events";
import FilterBar from "theme/components/Filter/FilterBar";
import NoResultText from "theme/components/NoResultText";
import Pagination from "theme/components/ListProducts/Pagination";
import PropTypes from "prop-types";
import { SearchEvents } from "core-web/libs/Events/constants";
// import SelectedFilters from "theme/components/Filter/SelectedFilters";
import SkeletonFilterBar from "theme/components/skeletons/FilterBar";
import SkeletonProductGrid from "theme/components/skeletons/ProductGrid";
import SpinnerIcon from "theme/components/icons/SpinnerIcon";
// import { Transition } from "react-transition-group";
import { isEmpty } from "core-web/utils/object";
import loadable from "@loadable/component";
import { scrollTo } from "core-web/utils/scrollFunctions";
import styled from "theme/libs/styled";
import useAboveBreakpoint from "core-web/hooks/useAboveBreakpoint";
import { useTranslation } from "react-i18next";

// const Filter = loadable(() => import("theme/components/Filter"));

const ProductGrid = loadable(() => import("theme/components/products/ProductGrid"), {
    fallback: <SkeletonProductGrid />,
});

const Spinner = styled(SpinnerIcon)`
    margin: auto;
`;

const ListProducts = ({
    config,
    isDistinct,
    response,
    isFirstModule,
    isLandingPage,
    gridContent,
    hasCategoryFilter,
    filterButtons,
}) => {
    const { t } = useTranslation();
    const isDesktop = useAboveBreakpoint("tabletLg");
    const [filterVisible, setFilterVisible] = useState(isDesktop);
    const wrapperRef = useRef({});
    const filterRef = useRef();
    const [shouldScroll, setShouldScroll] = useState(false);

    const {
        clear,
        filters,
        hitsCount,
        page,
        pageSize,
        pageCount,
        productsCount,
        offset,
        selectedFilters = [],
        sorts = [],
        handleChange = {},
        products,
    } = response;

    const [searchOpen, setSearchOpen] = useState(true);

    useEffect(() => {
        const toggleSearchListener = Events.subscribe(SearchEvents.TOGGLE_SEARCH, ({ searchOpen }) => {
            setSearchOpen(searchOpen);
        });
        return () => {
            Events.unsubscribe(toggleSearchListener);
        };
    }, []);

    const scrollToTop = (smooth = false) => {
        // Scroll back to top of wrapper on pagination
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop - 120,
            });
        }
    };

    const onFilterChange = () => {
        setShouldScroll(true);
        scrollToTop(true);
    };

    const filterOverlayData = {
        filters,
        handleChange: handleChange.filter,
        scrollToTop: onFilterChange,
        clear,
        hitsCount,
        config,
        searchOpen,
        selectedFilters,
        hasCategoryFilter,
    };

    const displayCampaignAreas = page === 0 && !selectedFilters.length;

    return (
        <Div as="section" ref={wrapperRef} rowGap={["64px", null, null, null, "88px"]} className="productGrid">
            {!isEmpty(response) ? (
                <>
                    <FilterBar
                        filterOverlayData={filterOverlayData}
                        handleChange={handleChange}
                        scrollToTop={onFilterChange}
                        clear={clear}
                        config={config}
                        hitsCount={hitsCount}
                        filterState={[filterVisible, setFilterVisible]}
                        shouldScrollState={[shouldScroll, setShouldScroll]}
                        sorts={sorts}
                        filterRef={filterRef}
                        filterButtons={filterButtons}
                    />

                    <Div ref={filterRef} mb={["64px", null, null, null, "88px"]}>
                        {/* <Above
                            breakpoint="tabletLg"
                            render={() => (
                                <Transition in={filterVisible} timeout={0}>
                                    {(state) => (
                                        <Filter
                                            className={state}
                                            filters={filters}
                                            scrollToTop={onFilterChange}
                                            handleChange={handleChange.filter}
                                            config={config}
                                            hasCategoryFilter={hasCategoryFilter}
                                        />
                                    )}
                                </Transition>
                            )}
                        /> */}
                        {!!products?.length ? (
                            <>
                                {/* <SelectedFilters
                                    filters={filters}
                                    filterConfig={config}
                                    selectedFilters={selectedFilters}
                                    handleChange={handleChange.filter}
                                    scrollToTop={onFilterChange}
                                    clear={clear}
                                /> */}
                                <ProductGrid
                                    displayCampaignAreas={displayCampaignAreas}
                                    isDistinct={isDistinct}
                                    isFirstModule={isFirstModule}
                                    isLandingPage={isLandingPage}
                                    products={products}
                                    gridContent={gridContent}
                                />
                            </>
                        ) : (
                            <Fragment>
                                {Object.keys(response).length === 0 && response.constructor === Object ? (
                                    // If response is an empty object show spinner
                                    <Spinner />
                                ) : (
                                    // If response returns but response.products is empty
                                    <NoResultText>{t("filter.product_count_none")}</NoResultText>
                                )}
                            </Fragment>
                        )}
                    </Div>

                    {typeof page === "number" && pageCount > 1 && (
                        <Pagination
                            handleClick={scrollToTop}
                            hits={productsCount}
                            hitsCount={hitsCount}
                            offset={offset}
                            page={page}
                            pageCount={pageCount}
                            pageSize={pageSize}
                            suffix={hitsCount === 1 ? t("general.product") : t("general.products")}
                        />
                    )}
                </>
            ) : (
                <>
                    <SkeletonFilterBar />
                    <SkeletonProductGrid />
                </>
            )}
        </Div>
    );
};

ListProducts.propTypes = {
    config: PropTypes.object.isRequired,
    gridContent: PropTypes.array,
    hasCategoryFilter: PropTypes.bool,
    isDistinct: PropTypes.bool,
    isFirstModule: PropTypes.bool,
    isLandingPage: PropTypes.bool,
    response: PropTypes.shape({
        clear: PropTypes.object,
        filters: PropTypes.arrayOf(PropTypes.object),
        handleChange: PropTypes.object,
        hasMore: PropTypes.bool,
        hitsCount: PropTypes.number,
        offset: PropTypes.number,
        page: PropTypes.number,
        pageCount: PropTypes.number,
        pageSize: PropTypes.number,
        productsCount: PropTypes.number,
        products: PropTypes.arrayOf(PropTypes.object).isRequired,
        selectedFilters: PropTypes.arrayOf(PropTypes.object),
        sorts: PropTypes.arrayOf(
            PropTypes.exact({ index: PropTypes.string, label: PropTypes.string, selected: PropTypes.bool }),
        ),
    }).isRequired,
    filterButtons: PropTypes.array,
};

ListProducts.defaultProps = {
    gridContent: [],
    hasCategoryFilter: false,
    isDistinct: true,
    isFirstModule: false,
    isLandingPage: false,
    filterButtons: [],
};

export default ListProducts;
